/**
 * What a draft editor's save is actually allowed to send.
 *
 * On `PATCH .../drafts/{id}`, `customerId` is not a value: it is an
 * instruction. The API reads `'customerId' in body`, and naming the key at all
 * — a different customer, or an explicit `null` — **re-addresses** the
 * document: `codiceDestinatario` and `pecDestinatario` are rebuilt from the
 * counterpart now named, so that the address a document is delivered to can
 * never outlive the party it belongs to. Omitting the key leaves the
 * addressing exactly as it stands.
 *
 * The editor sent the key on every save. With a customer selected the routing
 * was re-derived correctly and nothing showed; with none, it fell back to
 * `0000000`. So a draft prepared through the API for a recipient that has no
 * anagrafica record — an agent's draft, an ERP's draft, carrying a
 * `codiceDestinatario` of its own — lost that address the first time a human
 * opened it and clicked Salva without changing a thing. Promoted, the invoice
 * went to `0000000`: recoverable only from Fatture e Corrispettivi, with no
 * notification to the recipient and nothing on screen to say so.
 *
 * So the key travels only when the selection really changed. Clearing the
 * customer still clears the routing, because clearing it *is* a change — a
 * document that names nobody must not keep somebody's address.
 *
 * Kept free of Svelte imports so it can be unit-tested directly.
 *
 * @param {object} content - the rest of the patch: causale, lines, total
 * @param {object} selection
 * @param {string|null} selection.customerId - the select's value now ('' when empty)
 * @param {string|null} selection.loadedCustomerId - the value the draft was loaded with
 * @returns {object} the body to PATCH
 */
export function draftSavePatch(content, { customerId, loadedCustomerId }) {
  const patch = { ...content };
  const chosen = customerId || null;
  // Explicit null, never undefined: an omitted key means "keep what is
  // stored", and a stated null is what clears the counterpart.
  if (chosen !== (loadedCustomerId || null)) patch.customerId = chosen;
  return patch;
}

/**
 * A person's name out of the parts an anagrafica record may carry, or ''.
 *
 * Guarded rather than interpolated: `${nome} ${cognome}` on a record holding
 * neither renders the string "undefined undefined", and it renders it in the
 * one place an operator is trying to work out who the document is for.
 *
 * @param {{ragioneSociale?: string, nome?: string, cognome?: string}} row
 * @returns {string}
 */
function nameOf(row) {
  const ragione = (row?.ragioneSociale || '').trim();
  if (ragione) return ragione;
  return `${row?.nome || ''} ${row?.cognome || ''}`.trim();
}

/**
 * The counterparty options a draft editor may show, with the one the draft
 * actually names guaranteed to be among them.
 *
 * The select is fed the active *clienti*, which is right for choosing a new
 * counterpart and wrong for displaying the current one: a draft may name a
 * fornitore — as one prepared through the API for a foreign supplier does — a
 * cliente since deactivated, a record past the 200-row cap the list endpoint
 * applies, or one deleted outright. In every one of those the stored id matched
 * no option, so the select rendered empty and the editor showed a draft with no
 * counterpart at all.
 *
 * Find-then-push, and never a blind push: `{#each customers as c (c.id)}` is
 * keyed, and a duplicate key throws `each_key_duplicate` and blanks the page.
 * Returns a new array and mutates nothing.
 *
 * @param {Array<object>} customers - the options as loaded
 * @param {{customerId?: string|null, metadata?: object}} draft - the stored draft
 * @param {object|null} [fetched] - the record `GET /customers/:id` returned, when
 *   it answered; null when it 404ed or failed for any other reason
 * @returns {Array<object>}
 */
export function withStoredCounterpart(customers, draft, fetched = null) {
  const options = Array.isArray(customers) ? customers : [];
  const storedId = draft?.customerId;
  if (!storedId) return options;
  if (options.find((c) => c?.id === storedId)) return options;

  if (fetched) {
    // The id the select binds to, not the one the response happened to carry:
    // the option's value has to be the draft's stored `customerId` or it
    // preselects nothing, which is the whole bug.
    return [...options, { ...fetched, id: storedId }];
  }

  // No record left to read: the metadata snapshot is what the document itself
  // says about its counterpart, and it was written when the draft was. Left
  // empty when it says nothing — `customerOptionLabel()` owns the wording, so
  // no Italian is spelled out in this file.
  const snapshot = draft?.metadata?.cessionario || {};
  return [...options, {
    id: storedId,
    ragioneSociale: nameOf({ ...snapshot, ragioneSociale: snapshot.denominazione }),
    synthesized: true,
  }];
}

/**
 * How one counterparty reads in the select.
 *
 * The suffixes are the point: an option that is a fornitore, a deactivated
 * record or a row no longer in the anagrafica looks exactly like an ordinary
 * cliente otherwise, and the operator would have no way to tell why it is not
 * in the list they expected.
 *
 * @param {object} customer - an anagrafica row, or a synthesized one
 * @param {object} [labels] - the translated suffixes, so this file needs no i18n
 * @param {string} [labels.supplier] - "(fornitore)"
 * @param {string} [labels.inactive] - "(disattivato)"
 * @param {string} [labels.missing] - "(non in anagrafica)"
 * @param {string} [labels.unknown] - what an unnameable counterpart is called
 * @returns {string}
 */
export function customerOptionLabel(customer, labels = {}) {
  const name = nameOf(customer);
  const parts = [name || labels.unknown || ''];
  if (customer?.kind === 'fornitore' && labels.supplier) parts.push(labels.supplier);
  if (customer?.isActive === false && labels.inactive) parts.push(labels.inactive);
  // Only alongside a name: `labels.unknown` already says the record is gone,
  // and "Controparte non più in anagrafica (non in anagrafica)" says it twice.
  if (customer?.synthesized && name && labels.missing) parts.push(labels.missing);
  return parts.filter(Boolean).join(' ');
}
